import React, { Suspense, useContext, useEffect, useMemo } from 'react'
import 'expo-dev-client'
// Needed for web, since GestureDetector is using it
import 'setimmediate'
import './socket/socket-client' // Initialize the socket object first
import AppNavigator, { AppNavigatorScreens } from './navigation/app.navigator'
import {
  LinkingOptions,
  NavigationContainer,
  getStateFromPath,
} from '@react-navigation/native'
import { AuthContext, AuthContextProvider } from './contexts/auth-context'
import { RootSiblingParent } from 'react-native-root-siblings'
import { useFonts } from 'expo-font'
import { AuthClientProvider } from './contexts/auth-client-provider'
import { CurrentNodeProvider } from './contexts/current-node.provider'
import FeatureProvider from './contexts/feature.provider'
import 'intl-pluralrules'
import './i18n'
import { Platform, Text, View } from 'react-native'
import { PushTokenProvider } from './contexts/push-token.provider'
import * as Notifications from 'expo-notifications'
import * as TaskManager from 'expo-task-manager'
import * as Linking from 'expo-linking'
import { TaskManagerTaskBody } from 'expo-task-manager'
import { PushNotificationTrigger } from 'expo-notifications'
import { useTheme } from './hooks/theme.hook'
import { Provider as PaperProvider } from 'react-native-paper'
import { GlobalStylesProvider } from './contexts/global-styles.provider'
// import { VoteDetailsProvider } from './@iu/vote/VoteDetailsProvider' // TODO: VoteDetailsProvider component needs to be created or located
import { HeaderButtonProvider } from './contexts/header-button.provider'
import * as Updates from 'expo-updates'
import {
  ModalDialogProvider,
  useModalDialog,
} from './@ui/containers/modals/modal-dialogue.provider'
import { Env } from './env'
import { useTranslation } from 'react-i18next'
import { enableExperimentalWebImplementation } from 'react-native-gesture-handler'
import * as Sentry from '@sentry/react-native'
import * as SentryBrowser from '@sentry/browser'
import NotificationProvider from './contexts/notification.provider'
import { useNotifications } from './hooks/otifications.hook'
import { preloadUiSounds } from './utils/play-ui-sound.util'
import { ReplayNavigatorScreens } from './navigation/replay.navigator'
import { AdminNavigatorScreens } from './navigation/admin.navigator'
import { Provider as ReduxProvider } from 'react-redux'
import { store, persistor } from './reducers/store'
import { PersistGate } from 'redux-persist/integration/react'
import { WechatJSSDKProvider } from './contexts/wechat-jssdk.provider'

const isWeb = Platform.OS === 'web'

Sentry.init({
  environment: Env.BUILD_TYPE,
  dsn: Env.SENTRY_SDN || '',
  tracesSampleRate: 2.0,
  // integrations: isWeb
  //   ? [SentryBrowser.browserTracingIntegration()] // For web
  //   : [Sentry.reactNativeTracingIntegration()], // For React Native
  debug: true, // If `true`, Sentry will try to print out useful debugging information if something goes wrong with sending the event. Set it to `false` in production
})

if (Platform.OS == 'web') {
  enableExperimentalWebImplementation()
}

if (Platform.OS != 'web') {
  Notifications.setNotificationHandler({
    handleNotification: async () => ({
      shouldShowAlert: true,
      shouldPlaySound: true,
      shouldSetBadge: false,
    }),
  })

  const BACKGROUND_NOTIFICATION_TASK = 'BACKGROUND-NOTIFICATION-TASK'
  TaskManager.defineTask(BACKGROUND_NOTIFICATION_TASK, (body) => {
    const { data, error, executionInfo } =
      body as TaskManagerTaskBody<PushNotificationTrigger>

    if ('notification' in data) {
      // @ts-ignore
      const {
        notification: {
          data: { body },
        },
      } = data
      const parsed = JSON.parse(body)
      if ('pendingCount' in parsed) {
        console.log('Setting badge count to', parsed.pendingCount)
        Notifications.setBadgeCountAsync(parsed.pendingCount)
      }
    } else if ('payload' in data) {
    } else {
    }
    // Do something with the notification data
  })

  Notifications.registerTaskAsync(BACKGROUND_NOTIFICATION_TASK).then()
}

function getUrlFromNotification(notification?: Notifications.Notification) {
  console.log(JSON.stringify(notification, null, 4))

  // TODO: Sorry for the `?` chain, this seems conflicting with google login redirect
  const url =
    notification?.request?.content?.data?.type === 'contribution-pending'
      ? `decisions`
      : null
  return url ? Linking.createURL(url) : null
}

function Navigation() {
  const { loading } = useContext(AuthContext)
  // https://reactnavigation.org/docs/configuring-links
  const linking: LinkingOptions<any> = useMemo(() => {
    return {
      prefixes: [Linking.createURL('')],
      config: {
        initialRouteName: 'ClusterList',
        screens: {
          ...AppNavigatorScreens,
          Replay: {
            path: 'replay',
            screens: ReplayNavigatorScreens,
          },
          Admin: {
            path: 'admin',
            screens: AdminNavigatorScreens,
          },
        },
      },
      getStateFromPath: (path, options) => {
        // This fires when we access directly the URL
        const stateFromPath = getStateFromPath(path, options)

        if (!stateFromPath) {
          return stateFromPath
        }
        // We add an external param to know this is a direct link reload
        const result = {
          ...stateFromPath,
          routes: stateFromPath.routes.map((r) => ({
            ...r,
            params: { ...r.params, external: true },
          })),
        }
        return result
      },
      async getInitialURL() {
        // First, you may want to do the default deep link handling
        // Check if app was opened from a deep link
        const url = await Linking.getInitialURL()
        if (url != null) {
          return url
        }
        // Handle URL from expo push notifications
        const response = await Notifications.getLastNotificationResponseAsync()
        return getUrlFromNotification(response?.notification)
      },
      subscribe(listener) {
        // Listen to incoming links from deep linking
        const s1 = Linking.addEventListener('url', (event: { url: string }) => {
          listener(event.url)
        })

        // Listen to expo push notifications
        const subscription =
          Notifications.addNotificationResponseReceivedListener(
            (response: Notifications.NotificationResponse) => {
              // simple mapping of type to url for now
              const url = getUrlFromNotification(response.notification)
              if (url) {
                listener(url)
              }
            }
          )

        return () => {
          // Clean up the event listeners
          s1.remove()
          subscription.remove()
        }
      },
    }
  }, [])

  const theme = useTheme()
  const { t } = useTranslation('common')
  const { setDialog } = useModalDialog()
  const { setupNotifications } = useNotifications()

  useEffect(() => {
    if (Env.IS_DEVELOP) {
      return
    }

    async function onFetchUpdateAsync() {
      if (Platform.OS === 'web') {
        return
      }
      try {
        const update = await Updates.checkForUpdateAsync()

        if (update.isAvailable) {
          await Updates.fetchUpdateAsync()
          setDialog({
            title: t('Update available'),
            body: <Text>{t('New version available')}</Text>,
            primaryLabel: t('Update'),
            primaryAction: async () => {
              setDialog(null)
              await Updates.reloadAsync()
            },
          })
        }
      } catch (error) {
        console.log(`Error fetching latest Expo update: ${error}`)
        Sentry.captureException(error)
      }
    }

    onFetchUpdateAsync().then()
  }, [])

  useEffect(() => {
    setupNotifications()
  }, [setupNotifications])

  useEffect(() => {
    void preloadUiSounds()
  }, [])

  if (loading) {
    return null
  }
  return (
    <View style={{ flex: 1 }}>
      <NavigationContainer linking={linking} theme={theme}>
        <PushTokenProvider>
          <CurrentNodeProvider>
            <WechatJSSDKProvider>
              {/* TODO: VoteDetailsProvider component needs to be created or located */}
              {/* <VoteDetailsProvider> */}
              <HeaderButtonProvider>
                <NotificationProvider>
                  <AppNavigator />
                </NotificationProvider>
              </HeaderButtonProvider>
              {/* </VoteDetailsProvider> */}
            </WechatJSSDKProvider>
          </CurrentNodeProvider>
        </PushTokenProvider>
      </NavigationContainer>
    </View>
  )
}

function ThemedApp() {
  const theme = useTheme()

  return (
    <PaperProvider theme={theme}>
      <GlobalStylesProvider>
        <RootSiblingParent>
          <AuthContextProvider>
            <AuthClientProvider>
              <ModalDialogProvider>
                <Navigation />
              </ModalDialogProvider>
            </AuthClientProvider>
          </AuthContextProvider>
        </RootSiblingParent>
      </GlobalStylesProvider>
    </PaperProvider>
  )
}

const App = () => {
  useFonts({
    Nucleo: require('./assets/thumbup/fonts/Nucleo.ttf'),
  })

  return (
    <>
      <Suspense fallback={<View></View>}>
        <ReduxProvider store={store}>
          <PersistGate loading={null} persistor={persistor}>
            <FeatureProvider>
              <ThemedApp />
            </FeatureProvider>
          </PersistGate>
        </ReduxProvider>
      </Suspense>
    </>
  )
}

export default Sentry.wrap(App)
